import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { hasPermission, PERMISSIONS } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Helper: Mask account number to last 4 digits
const maskAccountNumber = (accountNumber) => {
  if (!accountNumber) return '••••';
  const str = String(accountNumber).replace(/\D/g, '');
  if (str.length < 4) return '••••';
  return '••••' + str.slice(-4);
};

// Helper: Format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function MerchantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('merchant-detail', '/merchants');

  const [merchant, setMerchant] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [owners, setOwners] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [additionalEntities, setAdditionalEntities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMerchantData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('=== MERCHANT DETAIL FETCH START ===');
      console.log('MERCHANT ID:', id);
      console.log('HAS TOKEN:', !!token);
      console.log('API_URL:', API_URL);

      const merchantRes = await axios.get(`${API_URL}/merchants/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      console.log('MERCHANT RES:', merchantRes.data);
      setMerchant(merchantRes.data);

      // Fetch all subrecords with defensive error handling
      // Individual failures won't block page load
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/merchants/${id}/addresses`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        axios.get(`${API_URL}/merchants/${id}/owners`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        axios.get(`${API_URL}/merchants/${id}/bank-accounts`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        axios.get(`${API_URL}/merchants/${id}/additional-entities`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        axios.get(`${API_URL}/deals?merchant_id=${id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
      ]);

      const [addressesRes, ownersRes, bankAccountsRes, entitiesRes, dealsRes] = results;

      // Log each response
      console.log('ADDRESSES RES STATUS:', addressesRes.status);
      if (addressesRes.status === 'fulfilled') {
        console.log('ADDRESSES DATA:', addressesRes.value.data);
        setAddresses(addressesRes.value.data);
      } else {
        console.log('ADDRESSES ERROR:', addressesRes.reason);
      }

      console.log('OWNERS RES STATUS:', ownersRes.status);
      if (ownersRes.status === 'fulfilled') {
        console.log('OWNERS DATA:', ownersRes.value.data);
        setOwners(ownersRes.value.data);
      } else {
        console.log('OWNERS ERROR:', ownersRes.reason);
      }

      console.log('BANK ACCOUNTS RES STATUS:', bankAccountsRes.status);
      if (bankAccountsRes.status === 'fulfilled') {
        console.log('BANK ACCOUNTS DATA:', bankAccountsRes.value.data);
        setBankAccounts(bankAccountsRes.value.data);
      } else {
        console.log('BANK ACCOUNTS ERROR:', bankAccountsRes.reason);
      }

      console.log('ADDITIONAL ENTITIES RES STATUS:', entitiesRes.status);
      if (entitiesRes.status === 'fulfilled') {
        console.log('ADDITIONAL ENTITIES DATA:', entitiesRes.value.data);
        setAdditionalEntities(entitiesRes.value.data);
      } else {
        console.log('ADDITIONAL ENTITIES ERROR:', entitiesRes.reason);
      }

      console.log('DEALS RES STATUS:', dealsRes.status);
      if (dealsRes.status === 'fulfilled') {
        const dealsData = dealsRes.value.data.deals || dealsRes.value.data;
        console.log('DEALS DATA:', dealsData);
        setDeals(dealsData);
      } else {
        console.log('DEALS ERROR:', dealsRes.reason);
      }

      console.log('=== MERCHANT DETAIL FETCH END ===');
    } catch (err) {
      setError('Failed to load merchant');
      console.error('Merchant fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchMerchantData();
  }, [fetchMerchantData]);

  // Set up unified header
  useEffect(() => {
    if (!merchant) return;

    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: merchant.legal_name,
      subtitle: merchant.dba_name || 'No DBA name',
      actions: [
        hasPermission(PERMISSIONS.DEAL_CREATE) && (
          <Link key="new-advance" to={`/deals/new?merchant_id=${id}`} style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            + New Advance
          </Link>
        ),
        hasPermission(PERMISSIONS.MERCHANT_EDIT) && (
          <Link key="edit" to={`/merchants/${id}/edit`} style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            Edit
          </Link>
        ),
        hasPermission(PERMISSIONS.MERCHANT_DELETE) && (
          <button key="delete" onClick={handleDeleteMerchant} style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.ERROR,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}>
            Delete
          </button>
        ),
      ].filter(Boolean),
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, id, handleBack, setHeaderConfig, resetHeaderConfig]);

  const handleDeleteMerchant = async () => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/merchants/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      navigate('/merchants');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete merchant');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          <p style={TYPOGRAPHY.BODY}>Loading merchant...</p>
        </div>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.ERROR }}>
          <p style={TYPOGRAPHY.BODY}>Merchant not found</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {error && (
        <div style={{
          padding: SPACING.LG,
          backgroundColor: '#fee2e2',
          borderRadius: RADIUS.DEFAULT,
          color: COLORS.ERROR,
          marginBottom: SPACING.XL,
        }}>
          {error}
        </div>
      )}

      {/* Grid Layout Section */}
      <div style={styles.gridLayout}>
        {/* Business Information Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Business Information</h3>
          </div>
          <div>
            <div style={{...styles.infoRow, paddingBottom: SPACING.SM}}>
              <div style={styles.label}>Legal Name</div>
              <div style={styles.value}>{merchant.legal_name || '—'}</div>
            </div>
            <div style={{...styles.infoRow, paddingBottom: SPACING.SM}}>
              <div style={styles.label}>DBA</div>
              <div style={styles.value}>{merchant.dba_name || '—'}</div>
            </div>
            <div style={{...styles.infoRow, paddingBottom: SPACING.SM}}>
              <div style={styles.label}>EIN</div>
              <div style={styles.value}>{merchant.ein || '—'}</div>
            </div>
            <div style={{...styles.infoRow, paddingBottom: SPACING.SM}}>
              <div style={styles.label}>Entity Type</div>
              <div style={styles.value}>{merchant.entity_type || '—'}</div>
            </div>
            <div style={{...styles.infoRow, paddingBottom: SPACING.SM}}>
              <div style={styles.label}>State of Formation</div>
              <div style={styles.value}>{merchant.state_of_formation || '—'}</div>
            </div>
            <div style={{...styles.infoRow, paddingBottom: 0}}>
              <div style={styles.label}>Created</div>
              <div style={styles.value}>{formatDate(merchant.created_at)}</div>
            </div>
          </div>
        </div>

        {/* Owners Card */}
        {owners.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Owners & Guarantors ({owners.length})</h3>
          </div>
          <div>
            {owners.map((owner, idx) => {
              // Find primary owner (highest ownership % or first)
              const isPrimary = owners.length > 0 && owner.ownership_percentage === Math.max(...owners.map(o => o.ownership_percentage || 0));
              return (
                <div key={idx} style={{...styles.infoRow, paddingTop: idx > 0 ? SPACING.MD : 0, borderTop: idx > 0 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none', flexDirection: 'column', alignItems: 'flex-start', gap: SPACING.MD}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center'}}>
                    <div>
                      <div style={styles.label}>
                        {owner.full_name || '—'} {isPrimary && owner.ownership_percentage > 0 ? '(Primary)' : ''}
                      </div>
                      {owner.title && (
                        <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, marginTop: SPACING.XS}}>
                          {owner.title}
                        </div>
                      )}
                    </div>
                    <div style={styles.value}>
                      {owner.ownership_percentage !== null && owner.ownership_percentage !== undefined ? `${owner.ownership_percentage}%` : '—'}
                    </div>
                  </div>
                  {(owner.email || owner.phone) && (
                    <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>
                      {owner.email && <div>{owner.email}</div>}
                      {owner.phone && <div>{owner.phone}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

        {/* Addresses Card */}
        {addresses.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Addresses ({addresses.length})</h3>
          </div>
          <div>
            {addresses.map((addr, idx) => (
              <div key={idx} style={{...styles.infoRow, paddingTop: idx > 0 ? SPACING.MD : 0, borderTop: idx > 0 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none', flexDirection: 'column', alignItems: 'flex-start', gap: SPACING.SM}}>
                <div style={{display: 'flex', gap: SPACING.SM, alignItems: 'center'}}>
                  <div style={styles.label}>
                    Address
                  </div>
                  {addr.is_primary && (
                    <span style={{...TYPOGRAPHY.LABEL, backgroundColor: COLORS.PRIMARY, color: COLORS.TEXT_WHITE, padding: `${SPACING.XS} ${SPACING.SM}`, borderRadius: RADIUS.FULL, fontSize: '11px'}}>
                      Primary
                    </span>
                  )}
                </div>
                <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_PRIMARY}}>
                  {addr.address_line_1 || '—'}{addr.address_line_2 ? ` ${addr.address_line_2}` : ''}<br />
                  {addr.city || '—'}, {addr.state || '—'} {addr.zip || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Bank Accounts Card */}
        {bankAccounts.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Bank Accounts ({bankAccounts.length})</h3>
          </div>
          <div>
            {bankAccounts.map((account, idx) => (
              <div key={idx} style={{...styles.infoRow, paddingTop: idx > 0 ? SPACING.MD : 0, borderTop: idx > 0 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none', flexDirection: 'column', alignItems: 'flex-start', gap: SPACING.SM}}>
                <div style={{display: 'flex', gap: SPACING.SM, alignItems: 'center', width: '100%', justifyContent: 'space-between'}}>
                  <div>
                    <div style={styles.label}>
                      {account.account_name || 'Bank Account'}
                    </div>
                    <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, marginTop: SPACING.XS}}>
                      {account.bank_name || '—'}
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: SPACING.SM, alignItems: 'center'}}>
                    {account.is_primary && (
                      <span style={{...TYPOGRAPHY.LABEL, backgroundColor: COLORS.PRIMARY, color: COLORS.TEXT_WHITE, padding: `${SPACING.XS} ${SPACING.SM}`, borderRadius: RADIUS.FULL, fontSize: '11px'}}>
                        Primary
                      </span>
                    )}
                    {!account.is_active && (
                      <span style={{...TYPOGRAPHY.LABEL, backgroundColor: '#e5e7eb', color: '#666666', padding: `${SPACING.XS} ${SPACING.SM}`, borderRadius: RADIUS.FULL, fontSize: '11px'}}>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_PRIMARY, fontFamily: 'monospace'}}>
                  {maskAccountNumber(account.account_number)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Additional Entities Card */}
        {additionalEntities.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Related Businesses ({additionalEntities.length})</h3>
          </div>
          <div>
            {additionalEntities.map((entity, idx) => (
              <div key={idx} style={{...styles.infoRow, paddingTop: idx > 0 ? SPACING.MD : 0, borderTop: idx > 0 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none', flexDirection: 'column', alignItems: 'flex-start', gap: SPACING.SM}}>
                <div>
                  <div style={styles.label}>{entity.legal_name || '—'}</div>
                  {entity.dba_name && (
                    <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, marginTop: SPACING.XS}}>
                      DBA: {entity.dba_name}
                    </div>
                  )}
                </div>
                {entity.ein && (
                  <div style={{...TYPOGRAPHY.BODY_SM, fontSize: TYPOGRAPHY.BODY_SM.fontSize}}>
                    <span style={{...TYPOGRAPHY.LABEL, color: COLORS.TEXT_SECONDARY}}>EIN</span>
                    <div style={{color: COLORS.TEXT_PRIMARY}}>{entity.ein}</div>
                  </div>
                )}
                {entity.business_address && (
                  <div style={{...TYPOGRAPHY.BODY_SM, fontSize: TYPOGRAPHY.BODY_SM.fontSize, marginTop: SPACING.SM, width: '100%'}}>
                    <span style={{...TYPOGRAPHY.LABEL, color: COLORS.TEXT_SECONDARY}}>Business Address</span>
                    <div style={{color: COLORS.TEXT_PRIMARY, whiteSpace: 'pre-wrap'}}>{entity.business_address}</div>
                  </div>
                )}
                {entity.contact_address && (
                  <div style={{...TYPOGRAPHY.BODY_SM, fontSize: TYPOGRAPHY.BODY_SM.fontSize, marginTop: SPACING.SM, width: '100%'}}>
                    <span style={{...TYPOGRAPHY.LABEL, color: COLORS.TEXT_SECONDARY}}>Contact Address</span>
                    <div style={{color: COLORS.TEXT_PRIMARY, whiteSpace: 'pre-wrap'}}>{entity.contact_address}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Advances Card - Full Width */}
        <div style={{...styles.card, ...styles.fullWidth}}>
          <div style={{...styles.cardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <h3 style={styles.cardTitle}>Advances ({deals.length})</h3>
            {hasPermission(PERMISSIONS.DEAL_CREATE) && (
              <Link to={`/deals/new?merchant_id=${id}`} style={{
                padding: `${SPACING.SM} ${SPACING.MD}`,
                backgroundColor: COLORS.PRIMARY_DARK,
                color: COLORS.TEXT_WHITE,
                border: 'none',
                borderRadius: RADIUS.DEFAULT,
                fontSize: TYPOGRAPHY.BODY_SM.fontSize,
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.2s ease',
              }}>
                + New Advance
              </Link>
            )}
          </div>
          {deals.length > 0 && (
          <div>
            {deals.slice(0, 5).map((deal, idx) => (
              <div
                key={deal.id}
                style={{
                  ...styles.infoRow,
                  paddingTop: idx > 0 ? SPACING.MD : 0,
                  borderTop: idx > 0 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/deals/${deal.id}`)}
              >
                <div>
                  <div style={{...styles.label, color: COLORS.PRIMARY_DARK}}>{deal.visual_deal_id || '—'}</div>
                  <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>
                    {deal.status || '—'}
                  </div>
                </div>
                <div style={styles.value}>
                  ${parseFloat(deal.working_data?.deal_terms?.purchase_price || 0).toLocaleString()}
                </div>
              </div>
            ))}
            {deals.length > 5 && (
              <Link to="/deals" style={{...TYPOGRAPHY.BODY_SM, color: COLORS.PRIMARY_DARK, textDecoration: 'none', marginTop: SPACING.MD, display: 'block'}}>
                View all advances →
              </Link>
            )}
          </div>
          )}
          {deals.length === 0 && (
            <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY, textAlign: 'center', padding: SPACING.MD}}>
              No advances yet
            </div>
          )}
        </div>
      </div>

      {/* Notes Section - Full Width, Bottom */}
      {merchant.notes && (
        <div style={{...styles.card, marginBottom: 0}}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Notes</h3>
          </div>
          <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_PRIMARY, lineHeight: '1.6'}}>
            {merchant.notes}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: `${SPACING.MD} ${SPACING.XL} ${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  pageTitle: {
    ...TYPOGRAPHY.H1,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  pageSubtitle: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_SECONDARY,
    margin: SPACING.SM + ' 0 0 0',
  },

  primaryButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'all 0.2s ease',
  },

  gridLayout: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: SPACING.XL,
    marginBottom: SPACING.XXXL,
  },

  fullWidth: {
    gridColumn: '1 / -1',
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.MD,
    boxShadow: SHADOWS.DEFAULT,
  },

  cardHeader: {
    marginBottom: SPACING.MD,
    paddingBottom: SPACING.SM,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  gridTwoCols: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.XL,
  },

  infoRow: {
    padding: `${SPACING.XS} 0`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  label: {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_SECONDARY,
  },

  value: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '600',
  },
};
